import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import CookieBanner from '../../components/feature/CookieBanner';
import StructuredData from '../../components/base/StructuredData';

const ContactoPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    localidad: '',
    servicio: '',
    mensaje: '',
    privacidad: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);

    document.title = 'Contacto Vaciado de Pisos Mollet del Vallès | JUSTCLEAN';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Contacta con JUSTCLEAN para vaciado de pisos en Mollet del Vallès. Presupuesto sin compromiso en 24h. ☎️ 602 36 59 96 📧 info@mollet.justclean.es'
      );
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.privacidad) {
      alert('Debes aceptar la política de privacidad para continuar');
      return;
    }

    if (formData.mensaje.length > 500) {
      alert('El mensaje no puede superar los 500 caracteres');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('nombre', formData.nombre);
      formBody.append('telefono', formData.telefono);
      formBody.append('email', formData.email);
      formBody.append('localidad', formData.localidad);
      formBody.append('servicio', formData.servicio);
      formBody.append('mensaje', formData.mensaje);

      const response = await fetch('https://readdy.ai/api/form/d4q75sbm5ik2goc1f2v0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          telefono: '',
          email: '',
          localidad: '',
          servicio: '',
          mensaje: '',
          privacidad: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const localidades = [
    'Mollet del Vallès',
    'Parets del Vallès',
    'Montmeló',
    'Montornès del Vallès',
    'La Llagosta',
    'Martorelles',
    'Granollers',
    'Santa Perpètua de Mogoda',
    'Sant Fost de Campsentelles',
    'Palau-solità i Plegamans',
    'Otra localidad',
  ];

  const servicios = [
    'Vaciado de Pisos y Casas',
    'Vaciado de Locales Comerciales',
    'Retirada de Muebles y Electrodomésticos',
    'Limpieza por Síndrome de Diógenes',
    'Pintura Final',
    'Pequeñas Reparaciones',
    'Otro servicio',
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    mainEntity: {
      '@type': 'LocalBusiness',
      name: 'JUSTCLEAN Mollet del Vallès',
      telephone: '+34602365996',
      email: 'info@mollet.justclean.es',
    },
  };

  return (
    <div className={`min-h-screen transition-opacity duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <StructuredData data={structuredData} />
      <Header />

      <main>
        <section
          className="relative h-80 flex items-center justify-center"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/4701d7bd09a49a47b5d3f3424911c9e9.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-dark/60"></div>
          <div className="relative z-10 text-center px-4">
            <nav className="text-white/70 text-sm mb-4">
              <Link to="/" className="hover:text-white">
                Inicio
              </Link>
              <span className="mx-2">&gt;</span>
              <span className="text-white">Contacto</span>
            </nav>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Contacta con JUSTCLEAN en Mollet del Vallès
            </h1>
            <p className="text-white/90 text-xl max-w-2xl mx-auto">
              Presupuesto sin compromiso en menos de 24 horas
            </p>
          </div>
        </section>

        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
              <div className="lg:col-span-2 bg-dark text-white rounded-2xl p-10">
                <h2 className="text-3xl font-bold mb-6">Hablemos de tu proyecto</h2>

                <p className="text-white/85 text-lg leading-relaxed mb-10">
                  Estamos aquí para ayudarte con cualquier servicio de vaciado de pisos, locales o limpieza en Mollet del Vallès y comarca. Contacta con nosotros y te daremos un presupuesto personalizado sin compromiso.
                </p>

                <div className="space-y-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary rounded-full flex-shrink-0">
                      <i className="ri-phone-fill text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Teléfono / WhatsApp</h3>
                      <a
                        href="tel:+34602365996"
                        className="text-white/85 hover:text-primary transition-colors text-lg cursor-pointer"
                      >
                        602 36 59 96
                      </a>
                      <p className="text-white/60 text-sm mt-1">Llámanos o escríbenos por WhatsApp</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary rounded-full flex-shrink-0">
                      <i className="ri-mail-fill text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Email</h3>
                      <a
                        href="mailto:info@mollet.justclean.es"
                        className="text-white/85 hover:text-primary transition-colors cursor-pointer"
                      >
                        info@mollet.justclean.es
                      </a>
                      <p className="text-white/60 text-sm mt-1">Respuesta en menos de 24 horas</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary rounded-full flex-shrink-0">
                      <i className="ri-time-fill text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Horario</h3>
                      <p className="text-white/85">Lunes a Viernes: 8:00 - 20:00</p>
                      <p className="text-white/85">Sábados: 9:00 - 14:00</p>
                      <p className="text-white/60 text-sm mt-1">Servicio urgente 24h disponible</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary rounded-full flex-shrink-0">
                      <i className="ri-map-pin-fill text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Zona de Servicio</h3>
                      <p className="text-white/85 leading-relaxed">
                        Mollet del Vallès, Parets del Vallès, Montmeló, Montornès, La Llagosta, Martorelles, Granollers, Santa Perpètua y toda la comarca del Vallès Oriental
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <div className="bg-white rounded-2xl p-10 shadow-lg border border-gray-100">
                  <h3 className="text-3xl font-bold text-dark mb-2">Solicita tu presupuesto gratuito</h3>
                  <p className="text-gray-600 mb-8">
                    Rellena el formulario y te contactaremos en menos de 24 horas con un presupuesto personalizado
                  </p>

                  <form onSubmit={handleSubmit} data-readdy-form id="contacto-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="nombre" className="block text-dark text-sm font-medium mb-2">
                          Nombre completo *
                        </label>
                        <input
                          type="text"
                          id="nombre"
                          name="nombre"
                          value={formData.nombre}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all text-sm"
                          placeholder="Tu nombre"
                        />
                      </div>

                      <div>
                        <label htmlFor="telefono" className="block text-dark text-sm font-medium mb-2">
                          Teléfono *
                        </label>
                        <input
                          type="tel"
                          id="telefono"
                          name="telefono"
                          value={formData.telefono}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all text-sm"
                          placeholder="602 36 59 96"
                        />
                      </div>
                    </div>

                    <div className="mb-6">
                      <label htmlFor="email" className="block text-dark text-sm font-medium mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all text-sm"
                        placeholder="tu@email.com"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="localidad" className="block text-dark text-sm font-medium mb-2">
                          Localidad *
                        </label>
                        <select
                          id="localidad"
                          name="localidad"
                          value={formData.localidad}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all text-sm cursor-pointer"
                        >
                          <option value="">Selecciona tu localidad</option>
                          {localidades.map((loc) => (
                            <option key={loc} value={loc}>
                              {loc}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="servicio" className="block text-dark text-sm font-medium mb-2">
                          Tipo de servicio *
                        </label>
                        <select
                          id="servicio"
                          name="servicio"
                          value={formData.servicio}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all text-sm cursor-pointer"
                        >
                          <option value="">Selecciona un servicio</option>
                          {servicios.map((serv) => (
                            <option key={serv} value={serv}>
                              {serv}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mb-6">
                      <label htmlFor="mensaje" className="block text-dark text-sm font-medium mb-2">
                        Mensaje / Detalles
                      </label>
                      <textarea
                        id="mensaje"
                        name="mensaje"
                        value={formData.mensaje}
                        onChange={handleInputChange}
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all text-sm resize-none"
                        placeholder="Cuéntanos más detalles sobre tu proyecto (máximo 500 caracteres)"
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-1 text-right">
                        {formData.mensaje.length}/500 caracteres
                      </p>
                    </div>

                    <div className="mb-6">
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="privacidad"
                          checked={formData.privacidad}
                          onChange={handleInputChange}
                          required
                          className="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">
                          Acepto la{' '}
                          <a href="#" className="text-primary hover:underline">
                            política de privacidad
                          </a>{' '}
                          y el tratamiento de mis datos personales *
                        </span>
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary/90 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-xl"></i>
                          <span>Enviando...</span>
                        </>
                      ) : (
                        <>
                          <span>Enviar Solicitud</span>
                          <i className="ri-send-plane-fill text-xl"></i>
                        </>
                      )}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-800 text-center font-medium">
                          ¡Gracias por tu solicitud! Te contactaremos en menos de 24 horas.
                        </p>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800 text-center font-medium">
                          Ha ocurrido un error. Por favor, inténtalo de nuevo o llámanos al 602 36 59 96.
                        </p>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-0 bg-white">
          <div className="w-full h-96">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d47616.89384847892!2d2.1844!3d41.5397!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a4bc0b0b0b0b0b%3A0x0!2sMollet%20del%20Vall%C3%A8s!5e0!3m2!1ses!2ses!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Mapa de Mollet del Vallès y zona de servicio"
            ></iframe>
          </div>
        </section>

        <section className="py-16 bg-secondary/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-primary rounded-full">
                  <i className="ri-time-line text-4xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-dark mb-2">Respuesta en 24h</h3>
                <p className="text-gray-700">
                  Te contactamos en menos de 24 horas con tu presupuesto personalizado
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-primary rounded-full">
                  <i className="ri-file-text-line text-4xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-dark mb-2">Presupuesto sin compromiso</h3>
                <p className="text-gray-700">
                  Todos nuestros presupuestos son gratuitos y sin ningún tipo de compromiso
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-primary rounded-full">
                  <i className="ri-shield-check-line text-4xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-dark mb-2">Servicio discreto y profesional</h3>
                <p className="text-gray-700">
                  Máxima confidencialidad y profesionalidad en todos nuestros servicios
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
      <CookieBanner />
    </div>
  );
};

export default ContactoPage;